import React, { useState, useEffect, useCallback } from "react";
import { Card, FormGroup, Label, Input, Alert } from "reactstrap";
import { Button } from "../../../components/Component";
import http from "../../../utils/http-common";
import swal from "sweetalert";

const BILLING_KEYS = {
  billing_invoice_prefix: {
    label: "Invoice number prefix",
    description: "Used in legal numbers: PREFIX-YEAR-SEQUENCE (letters, digits, hyphen only).",
  },
  billing_platform_legal_name: {
    label: "Issuer legal name",
    description: "Platform name shown in the PDF issuer block.",
  },
  billing_platform_legal_address: {
    label: "Issuer address",
    description: "Multiline address; each line appears on the PDF.",
  },
  billing_platform_vat_number: {
    label: "VAT / tax ID",
    description: "Shown on the PDF when set.",
  },
  billing_legal_footer: {
    label: "Legal footer text",
    description: "Footer on every invoice PDF. Leave empty to use the built-in default disclaimer.",
  },
};

const DEFAULT_DRAFT = {
  billing_invoice_prefix: "INV",
  billing_platform_legal_name: "",
  billing_platform_legal_address: "",
  billing_platform_vat_number: "",
  billing_legal_footer: "",
};

/**
 * Admin-only: platform billing copy for invoice PDFs and numbering (Settings keys, category admin).
 */
export function BillingLegalSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_DRAFT }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await http.get("/api/v1/settings");
        if (cancelled) return;
        const rows = res.data?.data || [];
        const map = {};
        rows.forEach((s) => {
          if (s?.key) map[s.key] = s.value;
        });
        const next = { ...DEFAULT_DRAFT };
        Object.keys(BILLING_KEYS).forEach((k) => {
          if (map[k] !== undefined && map[k] !== null) next[k] = String(map[k]);
        });
        setDraft(next);
      } catch (e) {
        if (!cancelled) swal("Error", e.response?.data?.message || "Failed to load settings", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const settings = Object.entries(BILLING_KEYS).map(([key, meta]) => ({
        key,
        value: draft[key] != null ? String(draft[key]) : "",
        label: meta.label,
        description: meta.description,
        category: "admin",
        type: "string",
        isPublic: false,
      }));
      await http.put("/api/v1/settings/bulk", { settings });
      swal("Saved", "Billing settings updated.", "success");
    } catch (e) {
      swal("Error", e.response?.data?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }, [draft]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <Card className="card-bordered">
      <div className="card-inner">
        <h5 className="card-title">Billing & invoices</h5>
        <p className="text-soft">
          Legal invoice number prefix, issuer details, and PDF footer. These values are used when generating invoice PDFs and when
          allocating numbers on issue.
        </p>
        <Alert color="info" className="mb-4">
          When you set status to <strong>issued</strong> without a manual number, the system assigns the next sequence using the
          prefix below.
        </Alert>
        {Object.entries(BILLING_KEYS).map(([key, meta]) => (
          <FormGroup key={key}>
            <Label>{meta.label}</Label>
            {key === "billing_platform_legal_address" || key === "billing_legal_footer" ? (
              <Input
                type="textarea"
                rows={key === "billing_legal_footer" ? 4 : 3}
                value={draft[key]}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
              />
            ) : (
              <Input
                type="text"
                value={draft[key]}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                maxLength={key === "billing_invoice_prefix" ? 32 : undefined}
              />
            )}
            <small className="text-soft d-block mt-1">{meta.description}</small>
          </FormGroup>
        ))}
        <Button color="primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}
